import { memo, useId, useState } from "react";
import { useReducedMotion } from "motion/react";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from "recharts";
import { cn } from "../../ui/utils";
import { ANIM_MS, CHART_HEIGHT, formatValue, shade } from "./chartTheme";
import { RadialLegend, RadialTooltip, type RadialLegendItem } from "./ChartChrome";
import type { NormalizedChart } from "./resolveChart";

// recharts types `activeShape` as ActiveShape<PieSectorDataItem>, which does not
// accept a narrowed param signature, so `any` is required here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderActiveShape(props: any) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 5}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
}

/** On-slice percentage label for pie charts — drawn at the slice mid-angle in
 *  white with a soft shadow for contrast, hidden for very small slices so labels
 *  never overlap. */
interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
}

function renderPercentLabel(p: PieLabelProps) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = p;
  if (!percent || percent < 0.08) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.62;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      fontSize={11}
      fontWeight={700}
      textAnchor="middle"
      dominantBaseline="central"
      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
    >
      {Math.round(percent * 100)}%
    </text>
  );
}

export const RadialChart = memo(function RadialChart({ chart }: { chart: NormalizedChart }) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState<number | null>(null);
  const uid = useId().replace(/:/g, "");

  const doughnut = chart.kind === "doughnut";
  const valueKey = chart.series[0].key;
  const size = CHART_HEIGHT.radial;

  const items: RadialLegendItem[] = chart.rows.map((r) => {
    const value = Number(r[valueKey]) || 0;
    return {
      label: String(r[chart.categoryKey] ?? ""),
      value,
      color: r.fill ?? "#6366f1",
      share: chart.total > 0 ? Math.round((value / chart.total) * 100) : 0,
    };
  });

  const centerValue =
    active != null ? formatValue(items[active]?.value, chart.unit) : formatValue(chart.total, chart.unit);
  const centerLabel = active != null ? `${items[active]?.share}% of total` : "Total";

  return (
    <div className={cn("flex items-center gap-3", chart.legend === "right" ? "flex-col @[440px]:flex-row" : "flex-col")}>
      <div
        className="relative shrink-0"
        // Native mouseleave on the chart box is the reliable "cursor left" signal:
        // Recharts' per-slice onMouseLeave can be skipped on fast exits, across the
        // paddingAngle gaps, or in the square-box corners, leaving hover state stuck.
        onMouseLeave={() => setActive(null)}
        style={{
          width: size,
          height: size,
          // Soft depth shadow lifts the pie off the card (harmless on dark, where
          // it is barely visible). The doughnut stays flat.
          filter: doughnut ? undefined : "drop-shadow(0 6px 12px rgba(15, 23, 42, 0.14))",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {/* Chart-level onMouseLeave also dismisses Recharts' internal tooltip state. */}
          <PieChart onMouseLeave={() => setActive(null)}>
            {/* Per-slice top-lit gradient gives the pie depth without altering the
                data color. The doughnut keeps its flat fills. */}
            <defs>
              {chart.rows.map((r, i) => {
                const c = r.fill ?? "#6366f1";
                return (
                  <linearGradient key={i} id={`${uid}-slice-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={shade(c, 0.24)} />
                    <stop offset="100%" stopColor={c} />
                  </linearGradient>
                );
              })}
            </defs>
            <Tooltip content={<RadialTooltip unit={chart.unit} total={chart.total} />} />
            <Pie
              data={chart.rows}
              dataKey={valueKey}
              nameKey={chart.categoryKey}
              cx="50%"
              cy="50%"
              innerRadius={doughnut ? "58%" : 0}
              outerRadius="86%"
              paddingAngle={2}
              cornerRadius={doughnut ? 0 : 4}
              stroke="none"
              label={doughnut ? undefined : renderPercentLabel}
              labelLine={false}
              activeIndex={active ?? undefined}
              activeShape={renderActiveShape}
              onMouseEnter={(_, i: number) => setActive(i)}
              onMouseLeave={() => setActive(null)}
              isAnimationActive={!reduce}
              animationDuration={ANIM_MS}
            >
              {chart.rows.map((r, i) => (
                <Cell
                  key={i}
                  fill={doughnut ? r.fill : `url(#${uid}-slice-${i})`}
                  stroke="none"
                  style={{ transition: "opacity 200ms ease", opacity: active == null || active === i ? 1 : 0.4 }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {doughnut && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-2 text-center">
            <span className="text-lg font-bold leading-none text-foreground">{centerValue}</span>
            <span className="mt-1 text-[0.625rem] font-medium leading-tight text-muted-foreground">
              {centerLabel}
            </span>
          </div>
        )}
      </div>

      {chart.legend !== "none" && (
        <RadialLegend items={items} unit={chart.unit} active={active} onActivate={setActive} />
      )}
    </div>
  );
});
